import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // WIMEXIA client-side filtering updates this attribute after checking
      // fitment brands against the app proxy.
      if (mutation.attributeName === 'data-wx-filter-state') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Cached normalized fitment vehicles by supplier/SKU.
   * @type {Record<string, Promise<Array<{brand: string, model: string}>>>}
   */
  #fitmentVehiclesCache = {};

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.wxFilteredRecommendations === 'true') {
      this.#applyWimexiaFitmentFilter();
    } else {
      this.#intersectionObserver.observe(this);
    }
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!window.Shopify?.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }

  /**
   * Normalizes family and brand values for stable storefront comparisons.
   * @param {string | undefined} value
   * @returns {string}
   */
  #normalize(value) {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('fr')
      .replace(/[._-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Fetches the distinct compatible vehicle brands for a supplier SKU.
   * @param {string | undefined} sku
   * @param {string | undefined} supplier
   * @returns {Promise<Set<string>>}
   */
  #fetchFitmentVehicles(sku, supplier) {
    const normalizedSku = String(sku ?? '').trim();
    const normalizedSupplier = String(supplier ?? '').trim();

    if (!normalizedSku) return Promise.resolve([]);

    const cacheKey = `${normalizedSupplier}|${normalizedSku}`;
    const cached = this.#fitmentVehiclesCache[cacheKey];
    if (cached) return cached;

    const params = new URLSearchParams({ sku: normalizedSku });
    if (normalizedSupplier) params.set('supplier', normalizedSupplier);

    const request = fetch(`/apps/wimexia/product-fitments?${params.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data || data.success !== true || !Array.isArray(data.vehicles)) return [];

        const seen = new Set();
        const vehicles = [];

        for (const vehicle of data.vehicles) {
          const brand = this.#normalize(vehicle?.manufacturerName);
          const model = this.#normalize(vehicle?.modelName);
          if (!brand && !model) continue;

          const key = `${brand}|${model}`;
          if (seen.has(key)) continue;
          seen.add(key);
          vehicles.push({ brand, model });
        }

        return vehicles;
      })
      .catch(() => []);

    this.#fitmentVehiclesCache[cacheKey] = request;
    return request;
  }

  #scoreCandidate(item, sourceVehicles, itemVehicles) {
    const sourceGroup = this.#normalize(this.dataset.wxSourceGroup);
    const sourceFamily = this.#normalize(this.dataset.wxSourceFamily);
    const itemGroup = this.#normalize(item.dataset.wxGroup);
    const itemFamily = this.#normalize(item.dataset.wxFamily);

    const sourceModels = new Set(
      sourceVehicles
        .filter((vehicle) => vehicle.brand && vehicle.model)
        .map((vehicle) => `${vehicle.brand}|${vehicle.model}`)
    );

    if (sourceModels.size === 0 || itemVehicles.length === 0) {
      return { exactVehicleModel: false, score: 0 };
    }

    const exactVehicleModel = itemVehicles.some((vehicle) => {
      return (
        vehicle.brand &&
        vehicle.model &&
        sourceModels.has(`${vehicle.brand}|${vehicle.model}`)
      );
    });

    if (!exactVehicleModel) {
      return { exactVehicleModel: false, score: 0 };
    }

    let score = 1000;
    if (sourceGroup && itemGroup === sourceGroup) score += 80;
    if (sourceFamily && itemFamily === sourceFamily) score += 50;

    return { exactVehicleModel: true, score };
  }

  #applyWimexiaFitmentFilter() {
    const items = Array.from(this.querySelectorAll('[data-wx-recommendation-item]')).filter(
      (item) => item instanceof HTMLElement
    );
    const maxProducts = Number(this.dataset.wxMaxProducts) || items.length;

    if (!items.length) {
      this.classList.add('hidden');
      this.dataset.wxFilterState = 'ready';
      return;
    }

    this.#fetchFitmentVehicles(this.dataset.wxSourceSku, this.dataset.wxSourceSupplier)
      .then(async (sourceVehicles) => {
        if (sourceVehicles.length === 0) {
          for (const item of items) item.hidden = true;
          this.classList.add('hidden');
          this.dataset.wxFilterState = 'ready';
          return;
        }

        const evaluated = await Promise.all(
          items.map(async (item, index) => {
            const itemVehicles = await this.#fetchFitmentVehicles(item.dataset.wxSku, item.dataset.wxSupplier);
            const match = this.#scoreCandidate(item, sourceVehicles, itemVehicles);

            return {
              item,
              index,
              exactVehicleModel: match.exactVehicleModel,
              score: match.score,
            };
          })
        );

        const ranked = evaluated
          .filter((entry) => entry.exactVehicleModel)
          .sort((a, b) => b.score - a.score || a.index - b.index);

        for (const entry of evaluated) {
          entry.item.hidden = true;
          entry.item.dataset.wxRecommendationScore = String(entry.score);
        }

        const parent = ranked[0]?.item?.parentElement;
        if (parent) {
          for (const entry of ranked) parent.appendChild(entry.item);
        }

        ranked.forEach((entry, index) => {
          entry.item.hidden = index >= maxProducts;
        });

        for (const list of this.querySelectorAll('.resource-list')) {
          if (list instanceof HTMLElement) {
            list.dataset.hasRecommendations = ranked.length > 0 ? 'true' : 'false';
          }
        }

        if (ranked.length === 0) {
          this.classList.add('hidden');
        } else {
          this.classList.remove('hidden');
        }

        this.dataset.wxFilterState = 'ready';
      })
      .catch((error) => {
        console.warn('WIMEXIA recommendations exact vehicle/model check failed:', error);

        for (const item of items) item.hidden = true;
        this.classList.add('hidden');
        this.dataset.wxFilterState = 'ready';
      });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
